import { GraphQLClient } from 'graphql-request';
import { gql } from 'urql';
import {
  CreateFileInput,
  CreateFilesArchiveInput,
  DeleteFileInput,
  FileList,
  Mutation,
  Query,
  QueryFileArgs,
  QueryFilesForHouseholdArgs,
} from '../generated/graphql';
import { FileFragment } from './fragments';

export const clientCreateFile = async (client: GraphQLClient, input: CreateFileInput) => {
  const res = await client.request<Pick<Mutation, 'createFile'>>(
    gql`
      ${FileFragment}
      mutation CreateFile($input: CreateFileInput!) {
        createFile(input: $input) {
          file {
            ...FileFragment
          }
        }
      }
    `,
    { input }
  );
  return res.createFile;
};

export const createFilesArchiveMutation = gql`
  mutation CreateFilesArchive($input: CreateFilesArchiveInput!) {
    createFilesArchive(input: $input) {
      downloadURL
      expiresAt
    }
  }
`;

export const clientCreateFilesArchive = async (client: GraphQLClient, input: CreateFilesArchiveInput) => {
  const res = await client.request<Pick<Mutation, 'createFilesArchive'>, { input: CreateFilesArchiveInput }>(
    createFilesArchiveMutation,
    { input }
  );
  return res.createFilesArchive;
};

export const clientGetFile = async (client: GraphQLClient, input: QueryFileArgs): Promise<Query['file']> => {
  const res = await client.request<Pick<Query, 'file'>, QueryFileArgs>(
    gql`
      ${FileFragment}
      query File($tenantID: ID!, $id: ID!) {
        file(tenantID: $tenantID, id: $id) {
          ...FileFragment
        }
      }
    `,
    input
  );
  return res.file;
};

export const clientDeleteFile = async (client: GraphQLClient, input: DeleteFileInput) => {
  const res = await client.request<Pick<Mutation, 'deleteFile'>, { input: DeleteFileInput }>(deleteFileMutation, {
    input,
  });
  return res.deleteFile;
};

const filesForHouseholdQuery = gql`
  ${FileFragment}
  query FilesForHousehold($householdID: ID!, $cursor: String) {
    filesForHousehold(householdID: $householdID, cursor: $cursor) {
      items {
        ...FileFragment
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const MAX_HOUSEHOLD_FILE_PAGES = 200;

export const clientGetFilesForHousehold = async (
  client: GraphQLClient,
  args: QueryFilesForHouseholdArgs
): Promise<FileList> => {
  const response = await client.request<{ filesForHousehold: FileList }>(filesForHouseholdQuery, args);
  const result = response.filesForHousehold;
  let pageCount = 1;

  while (result.pageInfo.hasNextPage) {
    if (!result.pageInfo.endCursor) {
      throw new Error('Household file pagination is missing an end cursor.');
    }
    if (++pageCount > MAX_HOUSEHOLD_FILE_PAGES) {
      throw new Error('Household file pagination exceeded the maximum page count.');
    }
    const nextResponse = await client.request<{ filesForHousehold: FileList }>(filesForHouseholdQuery, {
      ...args,
      cursor: result.pageInfo.endCursor,
    });
    result.items.push(...nextResponse.filesForHousehold.items);
    result.pageInfo = nextResponse.filesForHousehold.pageInfo;
  }

  return result;
};

export const deleteFileMutation = gql`
  mutation DeleteFile($input: DeleteFileInput!) {
    deleteFile(input: $input) {
      changeToken
      __typename
    }
  }
`;
